import { STORAGE_NAMESPACE } from "../config";
import {
  type AccountPoints,
  type AgreedPoints,
  parsePointsAnswer,
  pointsOf,
  pointsUrlOf,
  samePoints,
} from "../net/points";
import { jsonRecord, text } from "../net/protocol";
import {
  type Bank,
  bankOf,
  createEmptySave,
  deserialize,
  SAVE_KEY,
  type Save,
  serialize,
  withBank,
} from "../save/schema";

/**
 * AP1, what an account has earned kept with it, the page's half: the save's bank (purse, renown,
 * runs) synced with the account's on the Worker's `/points` (`server/points.ts`). At boot the
 * bank the Worker answers is written into the save before the game reads it; after a run that
 * banked, the page sends the new bank. Each sync shows the bank this browser last agreed with the
 * Worker for the account (`AGREED_POINTS_KEY`), so the Worker can tell which side moved. Pure but
 * for `post`.
 */

/** The bank this browser last agreed with the Worker, and for which account. */
export const AGREED_POINTS_KEY = `${STORAGE_NAMESPACE}:pointsAgreed`;

const MAX_ACCOUNT_CHARS = 128;

/** A store of the browser, as `PlatformAdapter.data`. */
export interface PointsStoreOfPage {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

/** The agreement kept for `account`; null for none or another account's. */
export function agreedFor(raw: string | null, account: string): AgreedPoints | null {
  const value = raw === null ? undefined : jsonRecord(raw);
  const points = pointsOf(value?.points);
  const version = value?.version;
  if (
    text(value?.account, MAX_ACCOUNT_CHARS) !== account ||
    points === undefined ||
    typeof version !== "number"
  ) {
    return null;
  }
  return { version, points };
}

/** The Worker's points desk as the page asks it. */
export interface PointsDesk {
  /** The bank the account keeps after `points` met it; undefined for a refusal or no answer. */
  sync(points: AccountPoints, base: AgreedPoints | null): Promise<AgreedPoints | undefined>;
}

/** The desk at `serverUrl` for the session; `post` resolves to the answer's text (a failure throws). */
export function pointsDeskOf(options: {
  readonly serverUrl: string;
  readonly session: string;
  readonly post: (url: string, body: string) => Promise<string>;
}): PointsDesk {
  const url = pointsUrlOf(options.serverUrl);
  return {
    async sync(points, base) {
      try {
        const answer = parsePointsAnswer(
          await options.post(url, JSON.stringify({ session: options.session, points, base })),
        );
        return answer === undefined || "refused" in answer ? undefined : answer;
      } catch {
        return undefined;
      }
    },
  };
}

function agree(store: PointsStoreOfPage, account: string, agreed: AgreedPoints): void {
  store.set(AGREED_POINTS_KEY, JSON.stringify({ account, ...agreed }));
}

/** The save on the store, or a fresh one stamped `nowMs`; a save that cannot be read is none. */
function savedOn(store: PointsStoreOfPage, nowMs: number): Save {
  try {
    const json = store.get(SAVE_KEY);
    return json === null ? createEmptySave(nowMs) : deserialize(json);
  } catch {
    return createEmptySave(nowMs);
  }
}

/** How a boot's sync ended. */
export type PointsSync = "kept" | "written" | "failed";

/**
 * Boot: the save's bank meets the account's. The bank the Worker keeps is written into the save
 * (`withBank`) and agreed; a Worker that refuses or does not answer leaves the save as it is.
 */
export async function syncSavedPoints(options: {
  readonly store: PointsStoreOfPage;
  readonly account: string;
  readonly desk: PointsDesk;
  readonly nowMs: number;
}): Promise<PointsSync> {
  const { store, account } = options;
  const save = savedOn(store, options.nowMs);
  const local: Bank = bankOf(save);
  const answer = await options.desk.sync(local, agreedFor(store.get(AGREED_POINTS_KEY), account));
  if (answer === undefined) {
    return "failed";
  }
  let synced: PointsSync = "kept";
  if (!samePoints(answer.points, local)) {
    store.set(SAVE_KEY, serialize(withBank(savedOn(store, options.nowMs), answer.points)));
    synced = "written";
  }
  agree(store, account, answer);
  return synced;
}

/**
 * After a run banked: the save's new bank goes to the Worker. It is agreed only when the Worker
 * kept exactly it; when another device moved the account meanwhile, the next boot's sync merges.
 */
export async function pushBankedPoints(options: {
  readonly store: PointsStoreOfPage;
  readonly account: string;
  readonly desk: PointsDesk;
  readonly bank: Bank;
}): Promise<void> {
  const { store, account, bank } = options;
  const answer = await options.desk.sync(bank, agreedFor(store.get(AGREED_POINTS_KEY), account));
  if (answer !== undefined && samePoints(answer.points, bank)) {
    agree(store, account, answer);
  }
}
